import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { isMobile, useTranslation } from '@pascal/pascal-core';
import { userSettingsStore } from '@pascal/pascal-core/shared/core/store/userSettingsStore';
import {
  freeBetStore,
  userInfoStore,
  gameConfigStore,
} from '@pascal/pascal-core/shared/core/store';

import { gameService } from '../../services';
import { gameStore } from '../../store/gameStore';
import { doBetStore } from '../../store/doBetStore';
import { autoBetStore } from '../../store/autoBetStore';
import { TranslationKeys } from '../../enums/translationKeys';
import { GAME_LOADER_DOT_COUNT, INFINITY_AUTO_BET } from '../../enums/constants';
import { webGameSceneDrawer } from '../../game/drawers/gameDrawer/gameSceneDrawer';

const BetSection = observer(() => {
  const { setDouble, setHalve, selectedAmount, isBetAccepted } = doBetStore;
  const { autoBetCount, isPlay, resetAutoBet } = autoBetStore;
  const { isAnimationOn } = userSettingsStore;
  const { activeFreeBet } = freeBetStore;
  const { balance } = userInfoStore;
  const { limits } = gameConfigStore;
  const { isBetsClosed } = gameStore;
  const btnRef = useRef(null);
  const t = useTranslation();

  useEffect(() => {
    if (!isAnimationOn) {
      webGameSceneDrawer.crazyWormIdleLongTexture?.state.clearTracks();
      webGameSceneDrawer.crazyWormIdleLongTexture?.skeleton.setToSetupPose();
    } else {
      setTimeout(() => {
        webGameSceneDrawer?.crazyWormIdleLongTexture?.destroy();
        webGameSceneDrawer?.setupCrazyWormIdleLong();
      }, 100);
    }
  }, [isAnimationOn]);

  const handleDoBet = useCallback(() => {
    // if (selectedAmount > balance) {
    //   notificationStore.setNotificationType(betResponseCodeByName.NotEnoughBalance);
    //   setTimeout(() => notificationStore.setNotificationType(null), 1000);
    // }
    if (isPlay) {
      resetAutoBet();
    } else if (!isBetsClosed) {
      gameService.sendBet();
    }
  }, [balance, selectedAmount, isBetsClosed, isPlay, resetAutoBet]);

  useEffect(() => {
    document.querySelector('.bet-button')?.focus();
  }, []);

  const handleKeyDownBet = useCallback(
    (e) => {
      if (e.code === 'Space' && !isBetsClosed && !isMobile()) {
        e.preventDefault();
        handleDoBet();
        e.stopPropagation();
      }
    },
    [isBetsClosed, isMobile]
  );

  useEffect(() => {
    if (!isMobile()) {
      document.addEventListener('keydown', handleKeyDownBet);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDownBet);
    };
  }, [isBetsClosed]);

  const betButtonTxt = useMemo(() => {
    if (isPlay) return t(TranslationKeys.Stop);
    if (activeFreeBet.id) return `${t(TranslationKeys.FreeBet)} ${activeFreeBet.remainingCount}`;

    return t(TranslationKeys.Bet);
  }, [isPlay, activeFreeBet, t]);

  return (
    <div className='action-btn-bet-row'>
      <div className='action-btn-block'>
        <button
          type='button'
          className='action-btn'
          onClick={setHalve}
          disabled={
            isBetsClosed ||
            selectedAmount <= limits?.minBet ||
            activeFreeBet.id ||
            (autoBetCount && isPlay)
          }
        >
          <span>1/2</span>
        </button>
      </div>
      <div className={`crazy-worm-big-btn-wrp ${isPlay ? 'stop' : ''}`}>
        <div className='crazy-worm-big-btn-holder'>
          {!!autoBetCount && (
            <span className='auto-bet-countdown'>
              {autoBetCount === INFINITY_AUTO_BET ? <i className='icon-infinite' /> : autoBetCount}
            </span>
          )}
          <button
            ref={btnRef}
            type='button'
            className={`bet-button ${isBetAccepted && !isPlay ? 'loading' : ''} ${
              !isBetsClosed && !autoBetCount ? 'pulse' : ''
            } ${isBetAccepted && !autoBetCount ? 'disabled' : ''}`}
            onClick={handleDoBet}
          >
            <span>
              <var>{betButtonTxt}</var>
            </span>
            {activeFreeBet.id && !activeFreeBet.isMinOddNull ? (
              <p>{`${t(TranslationKeys.MinOdd)} x${activeFreeBet.minOdd}`}</p>
            ) : (
              <></>
            )}
            {isBetAccepted && !isPlay && (
              <div className='loading-block'>
                {GAME_LOADER_DOT_COUNT.map((item) => (
                  <div key={item} />
                ))}
              </div>
            )}
          </button>
        </div>
      </div>
      <div className='action-btn-block'>
        <button
          className='action-btn'
          type='button'
          onClick={setDouble}
          disabled={
            isBetsClosed ||
            selectedAmount >= limits?.maxBet ||
            activeFreeBet.id ||
            (autoBetCount && isPlay)
          }
        >
          <span>x2</span>
        </button>
      </div>
    </div>
  );
});

export default BetSection;
